import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Lock, Save, Sparkles } from "lucide-react";

/**
 * IdealPessoalPanel — PRIVATE personal ideal, visible only to the owner.
 * Backed by GET/PUT /api/my-ideal. NEVER appears in directory, voice profile,
 * leaderboards, or any other public endpoint.
 *
 * Concept: a single sentence / motto / verse / poem that summarises a person's
 * personality ideal + mission + vision + values + principles, all in one.
 */
export default function IdealPessoalPanel() {
  const [text, setText] = useState("");
  const [original, setOriginal] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/my-ideal")
      .then((r) => { setText(r.data.ideal_pessoal || ""); setOriginal(r.data.ideal_pessoal || ""); setLoaded(true); })
      .catch((e) => { console.debug("ideal fetch failed:", e?.message); setLoaded(true); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/my-ideal", { ideal_pessoal: text });
      setOriginal(text);
      setEditing(false);
      toast.success("Ideal Pessoal guardado.");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <div className="kw-card p-5 border-[#EAB308]/30" data-testid="ideal-pessoal-panel">
      <div className="flex items-center justify-between mb-3 border-b border-[#222] pb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#EAB308]" />
          <div>
            <div className="kw-overline text-[#EAB308]">Só para ti</div>
            <h2 className="font-display text-xl font-black uppercase">Ideal Pessoal</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] border border-[#222] px-2 py-0.5">
            <Lock className="w-3 h-3" /> Privado
          </div>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="kw-btn-secondary !py-1.5 !px-3 text-xs" data-testid="ideal-edit-btn">
              {original ? "Editar" : "Definir"}
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => { setText(original); setEditing(false); }} className="kw-btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
              <button onClick={save} disabled={saving} className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="ideal-save-btn">
                <Save className="w-3 h-3" /> {saving ? "..." : "Guardar"}
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-[#737373] mb-3">
        A ideia predileta que Deus tem para ti — uma frase, um lema, um verso ou um poema que resume o teu ideal de
        personalidade, a tua missão, a tua visão, os teus valores e os teus princípios. <strong className="text-white">Ninguém mais vê isto.</strong>
      </p>

      {editing ? (
        <textarea
          rows={6}
          className="kw-input w-full font-serif italic leading-relaxed"
          placeholder='Ex.: "Servir com alegria, agir com verdade, amar sem medida." · ou um verso da Escritura · ou as tuas próprias palavras…'
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
          data-testid="ideal-textarea"
        />
      ) : original ? (
        <blockquote className="border-l-4 border-[#EAB308] pl-4 py-2 font-serif italic text-base text-white whitespace-pre-line leading-relaxed" data-testid="ideal-display">
          {original}
        </blockquote>
      ) : (
        <div className="border border-dashed border-[#333] p-6 text-center text-sm text-[#737373] italic">
          Ainda não definiste o teu Ideal Pessoal. Define-o quando estiveres pronto — pode ser uma só frase.
        </div>
      )}
    </div>
  );
}
